import encoderPath from 'opus-recorder/dist/encoderWorker.min.js?url';

const ENCODE_BUFFER_LENGTH = 4096;
const ENCODER_BASE_TIMEOUT_MS = 15000;
const ENCODER_TIMEOUT_PER_AUDIO_SECOND_MS = 1000;

const ENCODER_INIT_CONFIG = {
  command: 'init',
  encoderSampleRate: 48000,
  numberOfChannels: 1,
  encoderApplication: 2048,
  bufferLength: ENCODE_BUFFER_LENGTH,
  encoderFrameSize: 20,
  maxFramesPerPage: 40,
  resampleQuality: 3,
};

const decodeAudioData = async audioBlob => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioData = await audioContext.decodeAudioData(arrayBuffer);
  audioContext.close?.();
  return audioData;
};

// Voice notes are mono, which keeps the file small and is universally supported
const downmixToMono = audioBuffer => {
  const { numberOfChannels, length } = audioBuffer;
  if (numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }
  const mono = new Float32Array(length);
  for (let channel = 0; channel < numberOfChannels; channel += 1) {
    const data = audioBuffer.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      mono[i] += data[i] / numberOfChannels;
    }
  }
  return mono;
};

/**
 * Encodes an audio Blob into an Ogg/Opus Blob using the opus-recorder encoder worker.
 * WhatsApp renders Ogg/Opus audio as a native voice message (waveform, playback
 * speed and transcription), whereas mp3/wav are shown as generic audio files.
 * @param {Blob} audioBlob - The captured audio data as a Blob.
 * @returns {Promise<Blob>} - A Blob containing the Ogg/Opus encoded audio.
 */
export const convertToOpus = async audioBlob => {
  const audioBuffer = await decodeAudioData(audioBlob);
  const samples = downmixToMono(audioBuffer);

  return new Promise((resolve, reject) => {
    const worker = new Worker(encoderPath);
    const pages = [];

    const timeout = setTimeout(
      () => {
        worker.terminate();
        reject(new Error('Opus encoding timed out.'));
      },
      ENCODER_BASE_TIMEOUT_MS +
        audioBuffer.duration * ENCODER_TIMEOUT_PER_AUDIO_SECOND_MS
    );

    worker.onmessage = ({ data }) => {
      // The worker emits 'ready' only after the 'init' command creates the
      // encoder, so encoding starts here rather than before init.
      if (data.message === 'ready') {
        // Ogg Opus requires the OpusHead/OpusTags header pages up front.
        worker.postMessage({ command: 'getHeaderPages' });
        for (
          let offset = 0;
          offset < samples.length;
          offset += ENCODE_BUFFER_LENGTH
        ) {
          // `slice` (not `subarray`) gives each chunk its own ArrayBuffer so it
          // can be transferred. A view would make postMessage copy the whole
          // sample buffer on every iteration, which freezes the tab on
          // recordings longer than a few dozen seconds.
          const chunk = samples.slice(offset, offset + ENCODE_BUFFER_LENGTH);
          worker.postMessage({ command: 'encode', buffers: [chunk] }, [
            chunk.buffer,
          ]);
        }
        worker.postMessage({ command: 'done' });
      } else if (data.message === 'page') {
        pages.push(data.page);
      } else if (data.message === 'done') {
        clearTimeout(timeout);
        worker.terminate();
        resolve(new Blob(pages, { type: 'audio/ogg' }));
      }
    };

    worker.onerror = error => {
      clearTimeout(timeout);
      worker.terminate();
      reject(error);
    };

    worker.postMessage({
      ...ENCODER_INIT_CONFIG,
      originalSampleRate: audioBuffer.sampleRate,
    });
  });
};
